import { ChangeEvent, useCallback, useState, useRef, RefObject, useEffect, FocusEvent, MouseEvent, MouseEventHandler } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ClimbingArea } from "../types";
import { areaLink } from "../utils/climbingAreaUtils";
import fetchAreas from "../api/areas";
import debounce from 'lodash.debounce';

const MIN_SEARCH_LENGTH = 2;

enum SearchStatus {
    NoSearchStr,
    TooShort,
    FetchSent,
    ReceivedError,
    ReceivedEmpty,
    ReceivedAreas,
    AreaClicked,
    NoFocus
  }

function Search() {

    const [error, setError] = useState<string | null>(null);
    const [areas, setAreas] = useState<ClimbingArea[] | null>(null);
    const [searchStr, setSearchStr] = useState<string>("");
    const [loadedSearchStr, setLoadedSearchStr] = useState<string | null>(null);
    const [searchStatus, setSearchStatus] = useState<SearchStatus>(SearchStatus.NoSearchStr)
  
    const inputRef = useRef<HTMLInputElement>(null)

    const location = useLocation();

    useEffect(() => {
      setSearchStatus(SearchStatus.NoFocus);
      inputRef.current?.blur()
    }, [location]);
  
    
  
    // Get search status from the state
    const searchStatusFromState = (): SearchStatus => {
  
      // No need to change these
      if ([SearchStatus.FetchSent, SearchStatus.ReceivedError, SearchStatus.ReceivedAreas, SearchStatus.ReceivedEmpty].includes(searchStatus)) {
        return searchStatus;
      }
  
      // No search string
      if (searchStr.length === 0) {
        return SearchStatus.NoSearchStr
      }
  
      // Search string too short
      if (searchStr.length < MIN_SEARCH_LENGTH) {
        return SearchStatus.TooShort
      }
  
      // Search state was previously no focus but we are getting focus
      if (searchStatus === SearchStatus.NoFocus) {
        if (error !== null) {
          return SearchStatus.ReceivedError
        } else if (areas?.length === 0) {
          return SearchStatus.ReceivedEmpty
        } else {
          return SearchStatus.ReceivedAreas
        }
      }
  
      return SearchStatus.NoFocus
  
    }
    // On focus, display the search results if we already have some typed text
    const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
      setSearchStatus(searchStatusFromState());
    }

    const handleInputBlur = () => {
      setSearchStatus(SearchStatus.NoFocus);
    }
  
    const handleKeyboardEvent = (e: ChangeEvent<HTMLInputElement>): void => {
      setSearchStr(e.target.value);
      debouncedFetch(e.target.value);
    }
  
    const doFetch = (str: string) => {
  
      // Do not fetch if the search status is initial, don't need to show error message either
      if (str.length === 0) {
        setSearchStatus(SearchStatus.NoSearchStr);
        return;
      }
  
      if (str.length < MIN_SEARCH_LENGTH) {
        setSearchStatus(SearchStatus.TooShort); 
        return;
      }
  
      // Do not re-fetch if the string has not changed
      if (str === loadedSearchStr) {
        return;
      }
  
      setLoadedSearchStr(null);
      setError(null);
      setSearchStatus(SearchStatus.FetchSent);
      fetchAreas(str)
      .then(
        (areas: ClimbingArea[]) => {
          setAreas(areas);
          setLoadedSearchStr(str);
          setSearchStatus(areas.length === 0 ? SearchStatus.ReceivedEmpty : SearchStatus.ReceivedAreas)
        },
        (error) => {
          setLoadedSearchStr(str);
          setError(error);
          setSearchStatus(SearchStatus.ReceivedError);
        }
      )
    };
  
    const debouncedFetch = useCallback( // eslint-disable-line react-hooks/exhaustive-deps
      debounce(str => doFetch(str), 500
      ), []
    );
  
    const areaMouseDownHandler = (e: MouseEvent) => {
      e.preventDefault();
    }
  
    return (
      <div id="searchBar">
        <div id="searchBarInner">
          <span className="search__icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/></svg>
          </span>
          <SearchInput
            focusHandler={handleInputFocus}
            blurHandler={handleInputBlur}
            changeHandler={handleKeyboardEvent}
            inputRef={inputRef} />
          <SearchResults searchStr={searchStr}
            areas={areas} 
            error={error}
            searchStatus={searchStatus}
            loadedSearchStr={loadedSearchStr}
            areaMouseDownHandler={areaMouseDownHandler}/>
        </div>
      </div>
    );
  }
  
  interface SearchInputProps {
    focusHandler: (event: FocusEvent<HTMLInputElement>) => void;
    blurHandler: (event: FocusEvent<HTMLInputElement>) => void;
    changeHandler: (event: ChangeEvent<HTMLInputElement>) => void;
    inputRef: RefObject<HTMLInputElement>;
  }
  function SearchInput({ focusHandler, blurHandler, changeHandler, inputRef }: SearchInputProps) {
    return <input
      type="text"
      id="searchInput"
      placeholder="Search climbing areas"
      aria-label="Search climbing areas"
      autoComplete="off"
      onFocus={focusHandler}
      onBlur={blurHandler}
      onChange={changeHandler}
      ref={inputRef}
    />
  }
  
  interface SearchResultsProps {
    searchStr: string;
    areas: ClimbingArea[] | null;
    error: string | null;
    searchStatus: SearchStatus;
    loadedSearchStr: string | null;
    areaMouseDownHandler: (e: MouseEvent) => void;
  }
  function SearchResults(props: SearchResultsProps) {
  
    // States
    // Initial: help text
    // Text entered but no minimum length: no change
    // Text entered and fetch sent: loading
    // Fetch received with error: show error
    // Fetch received with no areas: no areas found
    // Fetch received with areas: area links
    var innerContent = <div></div>;
    var hidden = false;
  
    switch (props.searchStatus) {
      case SearchStatus.NoSearchStr: {
        hidden = true;
        break
      }
      case SearchStatus.FetchSent: {
        innerContent = <p className="search__message">Searching…</p>;
        break;
      }
      case SearchStatus.ReceivedError: {
        innerContent = <p className="search__message">Search isn’t working right now. Please try again in a moment.</p>;
        break;
      }
      case SearchStatus.ReceivedEmpty: {
        innerContent = (
          <p className="search__message">
            No areas match “{props.loadedSearchStr}”. Try part of the name, or{" "}
            <Link to="/forecast-by-state" onMouseDown={props.areaMouseDownHandler}>browse by state</Link>.
          </p>
        );
        break;
      }
      case SearchStatus.ReceivedAreas: {
        innerContent = <div><SearchResultsAreas areas={props.areas} mouseDownHandler={props.areaMouseDownHandler} /></div>;
        break;
      }
      case SearchStatus.TooShort: {
        innerContent = <p className="search__message">Type at least {MIN_SEARCH_LENGTH} letters to search.</p>;
        break;
      }
      case SearchStatus.AreaClicked: {
        break
      }
      case SearchStatus.NoFocus: {
        hidden = true;
        break
      }
    }
  
    // Polite live region so screen readers hear "Searching…" and the outcome
    return <div id="searchResults" className={hidden ? 'hidden' : ''} aria-live="polite">{innerContent}</div>
  }
  
  interface SearchResultsAreasProps {
    areas: ClimbingArea[] | null;
    mouseDownHandler: MouseEventHandler<HTMLAnchorElement>;
  }
  function SearchResultsAreas(props: SearchResultsAreasProps) {
  
    // Only shown with at least one result; the state tells same-named areas apart
    return (
      <div id="searchResultsAreas">
      { props.areas?.map((area) => (
          <div className="searchResultItem" key={"searchResultArea-" + area.areaId}>
            <Link to={areaLink(area)} onMouseDown={props.mouseDownHandler}>
              <span className="search__result-name">{area.name}</span>
              <span className="search__result-state">{area.adminAreaName}</span>
            </Link>
          </div>
      ))}
      </div>
    );
  }

  export default Search;